import moment from 'moment';
import { nextArrearsDue } from '../../utils/arrearsSchedule';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { FiTag, FiUser } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import creditService from '../../services/credit.service';
import creditPaymentService from '../../services/creditPayment.service';
import relWarrantyAssetService from '../../services/relWarrantyAssets.service';
import { getError } from '../../utils';
import CreditInfo from '../Credits/CreditInfo';
import CreditPaymentsCredit from '../Credits/CreditPaymentsCredit';
import CreditWarranties from '../Credits/CreditWarranties';
import { isWarrantiesEnabled } from '../../utils/featureFlags';
import HistoryModal from '../../components/HistoryModal';
import CreditDues from '../Credits/CreditDues';
import StatementModal from '../../components/StatementModal/StatementModal';
import { isClientStatementEnabled } from '../../utils/featureFlags';
import { CreditDeletionModal } from '../../components/CascadeDeletionModal';
import SingleObjectDocumentManager from '../../components/SingleObjectDocumentManager/SingleObjectDocumentManager';
import { useLabels } from '../../hooks/useLabels';
import { usePermissions } from '../../hooks/usePermissions';
import { EntityList, DetailHeader, DetailBreadcrumb, TabNav, Button } from '../../components/EntityList';

const emptyTabState = { rows: [], loading: true, error: '' };

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return { ...state, credit: action.payload, loading: false };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	default:
		return state;
	}
};

export default function CreditDetails() {
	const { t } = useLabels();
	const params = useParams();
	const navigate = useNavigate();
	const { canEdit, canDelete } = usePermissions();

	const { id, tab } = params;
	const [{ credit, loading }, dispatch] = useReducer(reducer, {
		credit: {},
		loading: true,
		error: '',
	});

	const fetchData = useCallback(async () => {
		dispatch({ type: 'FETCH_REQUEST' });
		try {
			const result = await creditService.getDetails(id);
			setExpiredCredit(
				result.data.closeDate
					? moment.utc(result.data.closeDate).isBefore(moment.utc())
					: result.data.endDate
						? moment.utc(result.data.endDate).isBefore(moment.utc())
						: false,
			);
			setAllDuesGenerated(
				result.data.generatedDebts && result.data.dues
					? result.data.generatedDebts.length >= result.data.dues
					: false,
			);
			dispatch({ type: 'FETCH_SUCCESS', payload: result.data });
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
		}
	}, [id]);

	const [duesState, setDuesState] = useState(emptyTabState);
	const [showStatement, setShowStatement] = useState(false);
	const [paymentsState, setPaymentsState] = useState(emptyTabState);
	const [warrantiesState, setWarrantiesState] = useState(emptyTabState);

	const fetchDues = useCallback(async () => {
		setDuesState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await creditService.getDuesForcredit(id);
			setDuesState({ rows: data, loading: false, error: '' });
		} catch (error) {
			setDuesState({ rows: [], loading: false, error: getError(error) });
		}
	}, [id]);

	const fetchPayments = useCallback(async () => {
		setPaymentsState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await creditPaymentService.getPaymentsForcredit(id);
			setPaymentsState({ rows: data, loading: false, error: '' });
		} catch (error) {
			setPaymentsState({ rows: [], loading: false, error: getError(error) });
		}
	}, [id]);

	const fetchWarranties = useCallback(async () => {
		setWarrantiesState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await relWarrantyAssetService.getDetails(id);
			setWarrantiesState({ rows: data, loading: false, error: '' });
		} catch (error) {
			setWarrantiesState({ rows: [], loading: false, error: getError(error) });
		}
	}, [id]);

	useEffect(() => {
		fetchData();
		fetchDues();
		fetchPayments();
		fetchWarranties();
	}, [id]);

	async function editCreditHandler() {
		navigate(`/AdminScreen/credits/editCredit/${credit._id}`);
	}

	const [expiredCredit, setExpiredCredit] = useState();
	const [allDuesGenerated, setAllDuesGenerated] = useState();
	const [showHistoryLog, setShowHistoryLog] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);

	function editCreditDateHandler() {
		navigate(`/AdminScreen/credits/credit/${id}/editStartDate`);
	}

	let nextExpirationDate = null;
	let lastExpirationDate;
	let generationDate = null;

	let periodicityNumMonths = 0;
	if (credit.periodicity === 'mensual') {
		periodicityNumMonths = 1;
	} else if (credit.periodicity === 'semestral') {
		periodicityNumMonths = 6;
	} else if (credit.periodicity === 'anual') {
		periodicityNumMonths = 12;
	} else if (credit.periodicity === 'otra') {
		periodicityNumMonths = credit.periodicityNumMonths;
	}

	let dueNumber = credit.generatedCredits ? Number(credit.generatedCredits.length) + 1 : 1;

	if (credit.generatedCredits && credit.generatedCredits.length > 0) {
		if (
			credit.generatedCredits.length > 0 &&
			credit.generatedCredits[credit.generatedCredits.length - 1].expiration
		) {
			credit.generatedCredits.sort((a, b) => moment.utc(a.expiration).diff(moment.utc(b.expiration)));

			lastExpirationDate = moment.utc(
				credit.generatedCredits[credit.generatedCredits.length - 1].expiration,
			);
		}
		nextExpirationDate = moment
			.utc(lastExpirationDate)
			.add(periodicityNumMonths, 'month')
			.format('DD-MM-YYYY');
		const nextExpirationMoment = moment.utc(nextExpirationDate, 'DD-MM-YYYY');
		generationDate = nextExpirationMoment.startOf('month').format('DD-MM-YYYY');
	} else if (credit.generatedCredits && credit.generatedCredits.length === 0 && credit.firstDueExpiration) {
		nextExpirationDate = moment.utc(credit.firstDueExpiration).format('DD-MM-YYYY');
		const nextExpirationMoment = moment.utc(nextExpirationDate, 'DD-MM-YYYY');
		generationDate = nextExpirationMoment.startOf('month').format('DD-MM-YYYY');
	}

	// Arrears: the period's due is generated on its last working day and
	// expires on arrearsDueDay of the next month (backend utils/dueStrategies.js).
	if (credit.accrual === 'arrears') {
		const arrearsNext = nextArrearsDue(credit, credit.generatedCredits || [], periodicityNumMonths);
		if (arrearsNext) ({ nextExpirationDate, generationDate } = arrearsNext);
	}

	const warrantiesEnabled = isWarrantiesEnabled();
	const TAB_SLUGS = ['info', ...(warrantiesEnabled ? ['warranties'] : []), 'payments', 'dues', 'documents'];
	const currentTab = TAB_SLUGS.includes(tab) ? tab : 'info';

	useEffect(() => {
		if (!loading && tab !== currentTab) {
			navigate(`/AdminScreen/credits/credit/${id}/${currentTab}`, { replace: true });
		}
	}, [loading, tab, currentTab, id]);

	const tabs = [
		{ id: 'info', label: t('title.details') },
		...(warrantiesEnabled
			? [{ id: 'warranties', label: t('entity.warranty.plural'), count: warrantiesState.rows.length }]
			: []),
		{ id: 'payments', label: t('entity.payment.plural'), count: paymentsState.rows.length },
		{ id: 'dues', label: t('entity.due.plural'), count: duesState.rows.length },
		{ id: 'documents', label: t('title.documents') },
	];

	function deleteCreditHandler() {
		setShowDeleteModal(true);
	}


	function handleCloseDeleteModal() {
		setShowDeleteModal(false);
	}

	const subline = [
		credit.concept && { label: t('entity.concept'), icon: <FiTag />, value: credit.concept },
		credit.owner?.name && { label: t('entity.account.debtor'), icon: <FiUser />, value: credit.owner.name },
	].filter(Boolean);

	return loading ? (
		<EntityList>
			<DetailPageLoader entityLabel={t('entity.credit')} />
		</EntityList>
	) : (
		<EntityList>
			<DetailBreadcrumb
				parentLabel={t('entity.credit.plural')}
				parentTo='/AdminScreen/credits'
				current={credit.customId}
			/>

			<DetailHeader
				name={credit.customId || ''}
				kicker={t('entity.credit')}
				subline={subline}
				actions={
					<>
						<Button onClick={() => setShowHistoryLog(true)}>{t('title.changeHistory')}</Button>
						<Button onClick={editCreditDateHandler}>{t('action.editStartDate')}</Button>
						{isClientStatementEnabled() && (
							<Button onClick={() => setShowStatement(true)}>{t('statement.title')}</Button>
						)}
						{canEdit() && (
							<Button variant='primary' onClick={editCreditHandler}>
								{t('action.edit')}
							</Button>
						)}
						{canDelete() && (
							<Button onClick={deleteCreditHandler}>{t('action.delete')}</Button>
						)}
					</>
				}
			/>

			<div className='tw:mt-5'>
				<TabNav
					tabs={tabs}
					current={currentTab}
					onChange={(nextTab) => navigate(`/AdminScreen/credits/credit/${id}/${nextTab}`)}
				/>
			</div>

			<div className='tw:mt-6'>
				{currentTab === 'info' && <CreditInfo credit={credit} />}
				{currentTab === 'warranties' && warrantiesEnabled && (
					<CreditWarranties
						creditId={id}
						{...warrantiesState}
						onRefetch={fetchWarranties}
					/>
				)}
				{currentTab === 'payments' && (
					<CreditPaymentsCredit
						creditId={id}
						{...paymentsState}
						onRefetch={fetchPayments}
					/>
				)}
				{currentTab === 'dues' && (
					<CreditDues
						{...duesState}
						onRefetch={fetchDues}
						expiredCredit={expiredCredit}
						allDuesGenerated={allDuesGenerated}
						nextExpirationDate={nextExpirationDate}
						dueNumber={dueNumber}
						generationDate={generationDate}
					/>
				)}
				{currentTab === 'documents' && (
					<div className='p-3'>
						<SingleObjectDocumentManager
							objectId={id}
							objectType='credit'
							documentType='credit'
						/>
					</div>
				)}
			</div>

			{isClientStatementEnabled() && (
				<StatementModal
					show={showStatement}
					kind='credit'
					record={credit}
					dues={duesState.rows}
					onClose={() => setShowStatement(false)}
				/>
			)}

			<HistoryModal model='credit' show={showHistoryLog} handleClose={() => setShowHistoryLog(false)} id={credit._id} isHistory={true} />

			<CreditDeletionModal
				show={showDeleteModal}
				record={{ _id: id }}
				onClose={handleCloseDeleteModal}
				onDeleted={() => navigate('/AdminScreen/credits')}
			/>

		</EntityList>
	);
}
